import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../../hooks';

// ----------------------------------------------------------------------

// 실시간 모니터링
const Dashboard = lazy(() => import('src/pages/dashboard/monitoring/dashboard'));
const SettopMonitoring = lazy(() => import('src/pages/dashboard/monitoring/settop'));
const CenterStatus = lazy(() => import('src/pages/dashboard/monitoring/center-status/status'));
const CenterStatusDetail = lazy(
  () => import('src/pages/dashboard/monitoring/center-status/detail')
);
const UsagePerSystem = lazy(() => import('src/pages/dashboard/monitoring/usage-per-system'));
const SystemUsage = lazy(() => import('src/pages/dashboard/monitoring/usage'));
const HelpDesk = lazy(() => import('src/pages/dashboard/monitoring/helpdesk'));
const HelpDeskDetail = lazy(() => import('src/pages/dashboard/monitoring/helpdesk/[id]'));

// 공지사항
const NoticeSend = lazy(() => import('src/pages/dashboard/notice/send/notice-register'));
const ReceiveNotice = lazy(() => import('src/pages/dashboard/notice/receive/receive-notice'));
const NoticeTemplate = lazy(() => import('src/pages/dashboard/notice/template/template'));
const NoticeTemplatePost = lazy(() => import('src/pages/dashboard/notice/send/template-register'));
const NoticeTemplateDetail = lazy(() => import('src/pages/dashboard/notice/template/detail'));
const NoticeHistory = lazy(() => import('src/pages/dashboard/notice/history/history'));
const NoticeHistoryDetail = lazy(() => import('src/pages/dashboard/notice/history/detail'));

// 방송 시스템
const Schedule = lazy(() => import('src/pages/dashboard/broadcast/schedule'));
const LinkedScheduleCopyPage = lazy(() => import('src/pages/dashboard/broadcast/schedule/linked'));
const LinkedScheduleWeekPage = lazy(
  () => import('src/pages/dashboard/broadcast/schedule/linked-week')
);
const Contents = lazy(() => import('src/pages/dashboard/broadcast/contents/contents'));
const ContentsDetail = lazy(() => import('src/pages/dashboard/broadcast/contents/detail'));
const BroadcastMonitoring = lazy(() => import('src/pages/dashboard/broadcast/monitoring'));
const ConnectionSettings = lazy(() => import('src/pages/dashboard/broadcast/connection-settings'));
const CenterBroadCast = lazy(() => import('src/pages/dashboard/broadcast/center-broadcast'));
const CenterBroadCastDetail = lazy(
  () => import('src/pages/dashboard/broadcast/center-broadcast/detail')
);
const BroadcastRecovery = lazy(() => import('src/pages/dashboard/broadcast/broadcast-recovery'));
const ViewingTimeStats = lazy(() => import('src/pages/dashboard/broadcast/viewing-time-stats'));

// 사용자 관리
const AdminAccount = lazy(() => import('src/pages/dashboard/management/admin-account'));
const AccountLog = lazy(() => import('src/pages/dashboard/management/account'));
const CenterAccount = lazy(() => import('src/pages/dashboard/management/center-account'));
const Authority = lazy(() => import('src/pages/dashboard/management/authority'));
const IntegrationApproval = lazy(
  () => import('src/pages/dashboard/management/integration-approval')
);
const AccountManagementPage = lazy(
  () => import('src/pages/dashboard/management/account-management')
);
const AccountManagementDetailPage = lazy(
  () => import('src/pages/dashboard/management/account-management-detail')
);

// 양방향 화상시스템 - 통합 (준비중)
const InteractiveIntegration = lazy(() => import('src/pages/dashboard/interactive/integration'));
const SettopUsage = lazy(() => import('src/pages/dashboard/interactive/settop-usage'));

// 이슈사항 관리
const IssueManagement = lazy(() => import('src/pages/dashboard/issue/issue-management'));
const ScenarioManagement = lazy(() => import('src/pages/dashboard/issue/scenario-management'));
const IssueUsageStatistics = lazy(() => import('src/pages/dashboard/issue/usage-statistics'));
const VideoCallLog = lazy(() => import('src/pages/dashboard/issue/video-call-log'));

// 이용 현황 관리
const LinkedBroadcastViewing = lazy(
  () => import('src/pages/dashboard/usage-status/linked-broadcast-viewing')
);

// 편의기능 관리
const HealthcareDeviceListView = lazy(() => import('src/pages/dashboard/healthcare/devices'));
const HealthcareDeviceUsageListView = lazy(() => import('src/pages/dashboard/healthcare/usage'));
const HealthcareDeviceUsageLogView = lazy(() => import('src/pages/dashboard/healthcare/usage-log'));
const HealthcareUsageLogDetailView = lazy(
  () => import('src/pages/dashboard/healthcare/usage-log-detail')
);

const LifecareDeviceListView = lazy(() => import('src/pages/dashboard/lifecare/devices'));
const LifecareDeviceUsageListView = lazy(() => import('src/pages/dashboard/lifecare/usage'));
const CompetitionListPage = lazy(() => import('src/pages/dashboard/lifecare/competition'));
const CompetitionDetailPage = lazy(() => import('src/pages/dashboard/lifecare/competition-detail'));
const CourseListPage = lazy(() => import('src/pages/dashboard/lifecare/course'));
const CourseDetailPage = lazy(() => import('src/pages/dashboard/lifecare/course-detail'));
const SmartTablePage = lazy(() => import('src/pages/dashboard/lifecare/smart-table'));
const SmartTableDetailPage = lazy(() => import('src/pages/dashboard/lifecare/smart-table-detail'));

// 서비스 운영
const IntegratedLogPage = lazy(
  () => import('src/pages/dashboard/service-operation/integrated-log')
);
const AccountLogPage = lazy(() => import('src/pages/dashboard/service-operation/account-log'));

// 설문 관리
const SurveyListPage = lazy(() => import('src/pages/dashboard/survey/list'));
const SurveyCreatePage = lazy(() => import('src/pages/dashboard/survey/create'));
const SurveyEditPage = lazy(() => import('src/pages/dashboard/survey/edit'));
const SurveyStatisticsPage = lazy(() => import('src/pages/dashboard/survey/statistics'));

// 내 계정
const PasswordChangePage = lazy(() => import('src/pages/dashboard/account/password-change'));
const ProfileEditPage = lazy(() => import('src/pages/dashboard/account/profile-edit'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const authLayout = () => <AuthGuard>{dashboardLayout()}</AuthGuard>;

const monitoringRoutes: RouteObject[] = [
  {
    path: 'monitoring',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <Dashboard />, index: true },
          { path: 'settop', element: <SettopMonitoring /> },
          { path: 'center-usage', element: <CenterStatus /> },
          { path: 'center-usage/:id', element: <CenterStatusDetail /> },
          { path: 'system-usage', element: <UsagePerSystem /> },
          { path: 'usage', element: <SystemUsage /> },
          { path: 'helpdesk', element: <HelpDesk /> },
          { path: 'helpdesk/:id', element: <HelpDeskDetail /> },
        ],
      },
    ],
  },
];

const noticeRoutes: RouteObject[] = [
  {
    path: 'notice',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <NoticeSend />, index: true },
          { path: 'template', element: <NoticeTemplate /> },
          { path: 'template/register', element: <NoticeTemplatePost /> },
          { path: 'template/:id', element: <NoticeTemplateDetail /> },
          { path: 'history', element: <NoticeHistory /> },
          { path: 'history/:id', element: <NoticeHistoryDetail /> },
          { path: 'send-notice', element: <NoticeSend /> },
          { path: 'receive-notice', element: <ReceiveNotice /> },
          { path: 'survey', element: <SurveyListPage /> },
          { path: 'survey/create', element: <SurveyCreatePage /> },
          { path: 'survey/edit/:id', element: <SurveyEditPage /> },
          { path: 'survey/statistics/:id', element: <SurveyStatisticsPage /> },
        ],
      },
    ],
  },
];

const interactiveRoutes: RouteObject[] = [
  {
    path: 'interactive',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <SettopMonitoring />, index: true },
          { path: 'settop', element: <SettopMonitoring /> },
          { path: 'usage', element: <SettopUsage /> },
          { path: 'video-call', element: <VideoCallLog /> },
          { path: 'integration', element: <InteractiveIntegration /> },
        ],
      },
    ],
  },
];

const usageStatusRoutes: RouteObject[] = [
  {
    path: 'usage-status',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <CenterStatus />, index: true },
          { path: 'center', element: <CenterStatus /> },
          { path: 'center/:id', element: <CenterStatusDetail /> },
          { path: 'system', element: <UsagePerSystem /> },
          { path: 'issue-stats', element: <IssueUsageStatistics /> },
          { path: 'linked-broadcast-viewing', element: <LinkedBroadcastViewing /> },
        ],
      },
    ],
  },
];

const broadcastRoutes: RouteObject[] = [
  {
    path: 'broadcast',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <Schedule />, index: true },
          { path: 'schedule/linked', element: <LinkedScheduleCopyPage /> },
          { path: 'schedule/linked/:serverId/:week', element: <LinkedScheduleWeekPage /> },
          { path: 'contents', element: <Contents /> },
          { path: 'contents/:id', element: <ContentsDetail /> },
          { path: 'connection-settings', element: <ConnectionSettings /> },
          { path: 'viewing-time-stats', element: <ViewingTimeStats /> },
          { path: 'monitoring', element: <BroadcastMonitoring /> },
          { path: 'center-broadcast', element: <CenterBroadCast /> },
          { path: 'center-broadcast/:id', element: <CenterBroadCastDetail /> },
          { path: 'recovery', element: <BroadcastRecovery /> },
        ],
      },
    ],
  },
];

const userRoutes: RouteObject[] = [
  {
    path: 'user',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <AdminAccount />, index: true },
          { path: 'account', element: <AccountLog /> },
          { path: 'center-account', element: <CenterAccount /> },
          { path: 'authority', element: <Authority /> },
        ],
      },
    ],
  },
];

const integrationRoutes: RouteObject[] = [
  {
    path: 'integration',
    element: authLayout(),
    children: [
      {
        children: [{ element: <IntegrationApproval />, index: true }],
      },
    ],
  },
];

const issueRoutes: RouteObject[] = [
  {
    path: 'issue',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <IssueManagement />, index: true },
          { path: 'scenario', element: <ScenarioManagement /> },
          { path: 'usage-statistics', element: <IssueUsageStatistics /> },
        ],
      },
    ],
  },
];

const videoCallRoutes: RouteObject[] = [
  {
    path: 'video-call',
    element: authLayout(),
    children: [
      {
        children: [{ element: <VideoCallLog />, index: true }],
      },
    ],
  },
];

const healthRoutes: RouteObject[] = [
  {
    path: 'health',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <HealthcareDeviceListView />, index: true },
          { path: 'usage', element: <HealthcareDeviceUsageListView /> },
          { path: 'usage-log', element: <HealthcareDeviceUsageLogView /> },
          { path: 'usage-log/:id', element: <HealthcareUsageLogDetailView /> },
        ],
      },
    ],
  },
];

const lifecareRoutes: RouteObject[] = [
  {
    path: 'lifecare',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <LifecareDeviceListView />, index: true },
          { path: 'usage', element: <LifecareDeviceUsageListView /> },
          { path: 'competition', element: <CompetitionListPage /> },
          { path: 'competition/:id', element: <CompetitionDetailPage /> },
          { path: 'course', element: <CourseListPage /> },
          { path: 'course/:id', element: <CourseDetailPage /> },
          { path: 'smart-table', element: <SmartTablePage /> },
          { path: 'smart-table/:id', element: <SmartTableDetailPage /> },
        ],
      },
    ],
  },
];

const serviceOperationRoutes: RouteObject[] = [
  {
    path: 'service-operation',
    element: authLayout(),
    children: [
      {
        children: [
          { element: <HelpDesk />, index: true },
          { path: 'helpdesk', element: <HelpDesk /> },
          { path: 'helpdesk/:id', element: <HelpDeskDetail /> },
          { path: 'issue', element: <IssueManagement /> },
          { path: 'scenario', element: <ScenarioManagement /> },
          { path: 'integrated-log', element: <IntegratedLogPage /> },
          { path: 'account-log', element: <AccountLogPage /> },
          { path: 'account', element: <AccountManagementPage /> },
          { path: 'account/:id', element: <AccountManagementDetailPage /> },
        ],
      },
    ],
  },
];

const accountRoutes: RouteObject[] = [
  {
    path: 'account',
    element: authLayout(),
    children: [
      {
        children: [
          { path: 'password', element: <PasswordChangePage /> },
          { path: 'profile', element: <ProfileEditPage /> },
        ],
      },
    ],
  },
];

export const dashboardRoutes: RouteObject[] = [
  ...monitoringRoutes,
  ...noticeRoutes,
  ...interactiveRoutes,
  ...usageStatusRoutes,
  ...broadcastRoutes,
  ...userRoutes,
  ...integrationRoutes,
  ...issueRoutes,
  ...videoCallRoutes,
  ...healthRoutes,
  ...lifecareRoutes,
  ...serviceOperationRoutes,
  ...accountRoutes,
];
